import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import imageUpload from "../../../services/upload";
import {
  FormControl,
  TextField,
  FormHelperText,
  Select,
  MenuItem,
} from "@mui/material";
import { IMAGE_PATH, instanceToken } from "../../../utils/constant";
import partners from "../../../services/partners";
import { CreateNewPrivilegeSchema } from "../../../schema/CreateNewPrivilegeSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  EditPageLayout,
  EditImageSection,
  EditFieldLabel,
  EditFormTopSection,
} from "../../../components/edit/EditPageLayout";

const imgFileTypes = ["image/gif", "image/jpeg", "image/png", "image/svg+xml"];

export default function CreatePrivilege({ homeAlert }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState("");
  const [imageFileUrl, setImageFileUrl] = useState("");
  const [values, setValues] = useState({ image_url: "" });
  const [errorMessage, setErrorMessage] = useState({});
  const [, setPartner] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CreateNewPrivilegeSchema),
    initialValues: {
      name_en: "",
      name_mm: "",
      address_en: "",
      address_mm: "",
      phone: "",
      phone_note: "",
      category: "",
      promotion: "",
      link: "",
    },
  });

  async function getImageUrl(imgData) {
    const response = await imageUpload.getImageUrl(
      instanceToken.token,
      imgData
    );
    const result = response.data;
    const path = IMAGE_PATH + result?.imageName;
    setImageFileUrl(result?.imageUploadUrl);
    setValues({
      ...values,
      image_url: path,
    });
  }

  const changeFile = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const img = e.target.files[0];
      if (!imgFileTypes.includes(img.type)) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Please select image. (PNG, JPG, JPEG, GIF, ...)",
        });
        return;
      }
      if (img.size > 204800) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Image file size must be smaller than 200KB.",
        });
        return;
      }
      setImageFile(img);
      setImagePreview(URL.createObjectURL(img));
      getImageUrl(img);
    }
  };

  const changeCategory = (e) => {
    setCategory(e.target.value);
    setValue("category", e.target.value);
  };

  const formData = new FormData();

  const handleCreate = async (data) => {
    formData.append("name_en", data?.name_en);
    formData.append("name_mm", data?.name_mm);
    formData.append("address_en", data?.address_en);
    formData.append("address_mm", data?.address_mm);
    formData.append("phone", data?.phone);
    formData.append("phone_note", data?.phone_note);
    formData.append("link", data?.link);
    formData.append("category", data?.category);
    formData.append("image_data", "");
    formData.append("image_url", values?.image_url);
    formData.append("promotion", data?.promotion);

    try {
      setLoading(true);
      await imageUpload.uploadImage(imageFileUrl, imageFile);
      const response = await partners.postPartner(
        instanceToken.token,
        formData
      );
      setPartner(response.data);
      navigate("/privileges");
      homeAlert("New Privilege have been created.", false);
    } catch (error) {
      console.log(error);
      homeAlert("Error on server.", true);
    } finally {
      setLoading(false);
      setErrorMessage({});
    }
  };

  return (
    <EditPageLayout
      title="Create Privilege"
      subtitle="Add privilege details and contact information."
      breadcrumbs={[
        { label: "Privileges", path: "/privileges" },
        { label: "Create" },
      ]}
      listPath="/privileges"
      loading={loading}
      submitLabel="Create"
      onSubmit={handleSubmit(handleCreate)}
    >
      <EditFormTopSection
        image={
          <EditImageSection
            uploadLabel="Privilege Image"
            previewLabel="Image Preview"
            imagePreview={imagePreview}
            onFileChange={changeFile}
            error={errorMessage["image_url"]}
            recommendedSize="340 x 180 px (1.8:1)"
          />
        }
      >
        <div>
          <EditFieldLabel required>Name (English)</EditFieldLabel>
          <TextField
            fullWidth
            id="name_en"
            {...register("name_en")}
            error={!!errors.name_en}
            helperText={errors.name_en?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Name (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            id="name_mm"
            {...register("name_mm")}
            error={!!errors.name_mm}
            helperText={errors.name_mm?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Address (English)</EditFieldLabel>
          <TextField
            fullWidth
            id="address_en"
            {...register("address_en")}
            error={!!errors.address_en}
            helperText={errors.address_en?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Address (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            id="address_mm"
            {...register("address_mm")}
            error={!!errors.address_mm}
            helperText={errors.address_mm?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Phone</EditFieldLabel>
          <TextField
            fullWidth
            id="phone"
            {...register("phone")}
            error={!!errors.phone}
            helperText={errors.phone?.message}
          />
        </div>
        <div>
          <EditFieldLabel>Phone Note</EditFieldLabel>
          <TextField
            fullWidth
            id="phone_note"
            {...register("phone_note")}
            error={!!errors.phone_note}
            helperText={errors.phone_note?.message}
          />
        </div>
        <div>
          <EditFieldLabel>Link</EditFieldLabel>
          <TextField
            fullWidth
            id="link"
            {...register("link")}
            error={!!errors.link}
            helperText={errors.link?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Category</EditFieldLabel>
          <FormControl fullWidth error={!!errors.category}>
            <Select value={category} onChange={changeCategory} displayEmpty>
              <MenuItem value="" disabled>
                Select category
              </MenuItem>
              <MenuItem value="Hospital">Hospital</MenuItem>
              <MenuItem value="Private-Hospital">Private-Hospital</MenuItem>
              <MenuItem value="Education">Education</MenuItem>
              <MenuItem value="Fitness">Fitness</MenuItem>
              <MenuItem value="Hotel">Hotel</MenuItem>
            </Select>
            <FormHelperText error>
              {errors.category?.message || errorMessage["category"]}
            </FormHelperText>
          </FormControl>
        </div>
        <div>
          <EditFieldLabel>Promotion</EditFieldLabel>
          <TextField
            fullWidth
            id="promotion"
            {...register("promotion")}
            error={!!errors.promotion}
            helperText={errors.promotion?.message}
          />
        </div>
      </EditFormTopSection>
    </EditPageLayout>
  );
}
